import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Typography, Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from './Header';
import CodeBlock from './codeBlock';

const SetupMikrotik = () => {
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || 'TENANT';

  const markdownContent = `Welcome! This guide will help you configure your Mikrotik router for Hotspot and PPPoE services. Follow each step carefully.

---

## 📋 Prerequisites

Before you begin, make sure you have:

- Access to your Mikrotik router via Winbox or WebFig
- Your tenant identifier: **${tenant}.nasradius.com**
- Basic knowledge of Mikrotik RouterOS

---

## 🟦 Part 1: Hotspot Setup

### Step 1: Download the Hotspot Configuration Script

1. Open **Winbox** or **WebFig** and connect to your Mikrotik router
2. Go to **Terminal** (or press \`Ctrl+T\` in Winbox)
3. Copy and paste the following command:

\`\`\`bash
:local done false; :for i from=1 to=10 do={ :if ($done) do={ :put "✔ Done"; :delay 1s; } else={ :put ("Attempt " . $i); :do { /tool fetch url="https://${tenant}.nasradius.com/api/onboarding/mikrotik-scripts/${tenant}/hotspot.rc" mode=https dst-path=hotspot.rc keep-result=yes } on-error={ :delay 1s }; :if ([:len [/file find name="hotspot.rc"]] > 0 && [/file get "hotspot.rc" size] > 0) do={ :put "✔ Download OK"; :set done true }; :delay 2s } }
\`\`\`

4. Press **Enter** and wait for the download to complete
5. You should see: \`status: finished\`

**Verify the file was downloaded:**

\`\`\`bash
/file print where name="hotspot.rc"
\`\`\`

You should see the file listed. If not, check your internet connection and try again.

---

### Step 2: Run the Hotspot Configuration

1. In the same Terminal window, run:

\`\`\`bash
/import hotspot.rc
\`\`\`

2. Wait for the script to complete (this may take 30-60 seconds)
3. You'll see various configuration messages as it sets up:
   - Hotspot IP address: **10.10.0.1/16**
   - DHCP server for hotspot clients
   - DNS configuration
   - RADIUS authentication
   - WireGuard VPN (if not already installed)
   - Hotspot HTML pages

**What this does:**
- ✅ Configures hotspot on IP 10.10.0.1/16
- ✅ Sets up DHCP for automatic IP assignment
- ✅ Configures DNS to ${tenant}.wifi
- ✅ Connects to RADIUS server for authentication
- ✅ Downloads custom hotspot login pages

---

## 🟩 Part 2: PPPoE Setup

### Step 1: Download the PPPoE Configuration Script

1. In the Terminal, copy and paste:

\`\`\`bash
/tool fetch url="https://${tenant}.nasradius.com/api/onboarding/mikrotik-files/${tenant}/pppoe.rc" mode=https dst-path=pppoe.rc
\`\`\`

2. Press **Enter** and wait for completion
3. Verify the file:

\`\`\`bash
/file print where name="pppoe.rc"
\`\`\`

---

### Step 2: Run the PPPoE Configuration

1. Run the import command:

\`\`\`bash
/import pppoe.rc
\`\`\`

2. Wait for the configuration to complete

**What this does:**
- ✅ Configures PPPoE server
- ✅ Creates IP pool: 10.11.0.10 - 10.11.0.254
- ✅ Sets up RADIUS authentication (ports 1812, 1813)
- ✅ Configures RADIUS accounting (ports 1816, 1617)
- ✅ Sets up WireGuard VPN if needed
- ✅ Configures firewall rules

---

## ✅ Verification

After completing both parts, verify your setup:

1. **Check Hotspot:**
   \`\`\`bash
   /ip hotspot print
   \`\`\`
   You should see hotspot configured on an interface.

2. **Check PPPoE Server:**
   \`\`\`bash
   /interface pppoe-server server print
   \`\`\`
   You should see the PPPoE server enabled.

3. **Check RADIUS:**
   \`\`\`bash
   /radius print
   \`\`\`
   You should see RADIUS client configured.

---

## 🆘 Troubleshooting

**Problem:** Script download fails
- **Solution:** Check your internet connection and firewall settings

**Problem:** Import command shows errors
- **Solution:** Make sure you have admin privileges and RouterOS is up to date

**Problem:** Hotspot/PPPoE not working
- **Solution:** Check your interface names and make sure they match your physical setup

---

## 📞 Need Help?

If you encounter any issues, please contact support +254724440202

---

**🎉 Congratulations!** Your Mikrotik router is now configured and ready to use with NasRadius.
`;

  return (
    <>
      <Header />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #e8f4fc 0%, #d4f0f5 100%)',
          padding: { xs: '20px 16px', sm: '40px 24px', md: '60px 32px' },
        }}
      >
        <Container
          maxWidth="md"
          sx={{ marginTop: '40px', padding: { xs: '20px', md: '40px' } }}
        >
        <Box
          sx={{
            padding: { xs: '20px', sm: '30px', md: '40px' },
            backgroundColor: '#fff',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
            borderRadius: '15px',
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
              fontWeight: 700,
              background: 'linear-gradient(90deg, #19b594, #0b74c1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              marginBottom: '20px',
            }}
          >
            Mikrotik Configuration Guide
          </Typography>

          <ReactMarkdown
            children={markdownContent}
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, ...props }) => (
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    fontWeight: 700,
                    marginTop: '30px',
                    marginBottom: '16px',
                    color: '#2C3E50',
                  }}
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    fontWeight: 600,
                    marginTop: '24px',
                    marginBottom: '12px',
                    color: '#2C3E50',
                  }}
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    fontWeight: 600,
                    marginTop: '20px',
                    marginBottom: '10px',
                    color: '#2C3E50',
                  }}
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    marginBottom: '16px',
                    color: '#4A4A4A',
                    lineHeight: 1.6,
                  }}
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <Box
                  component="ul"
                  sx={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    marginBottom: '16px',
                    paddingLeft: '24px',
                    color: '#4A4A4A',
                  }}
                  {...props}
                />
              ),
              li: ({ node, ordered, ...props }) => (
                <li>
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                      color: '#4A4A4A',
                    }}
                    {...props}
                  />
                </li>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0b74c1',
                    textDecoration: 'underline',
                    fontWeight: 500,
                  }}
                >
                  {children}
                </a>
              ),
              code: CodeBlock,
              hr: ({ node, ...props }) => (
                <Box
                  component="hr"
                  sx={{
                    border: 'none',
                    borderTop: '2px solid #e0e0e0',
                    margin: '24px 0',
                  }}
                  {...props}
                />
              ),
            }}
          />
        </Box>
        </Container>
      </Box>
    </>
  );
};

export default SetupMikrotik;

